import type { ReactNode, JSX } from 'react'

import { createEngagementContext } from '../../context/EngagementContext'

import type { CommentData } from '../fragments/commentData'

/**
 * The full comment shape provided through `CommentContext`. Card-tree
 * consumers derive from this alias instead of the codegen union; the shape
 * itself lives with the fragment in {@link ../fragments/commentData} so
 * producers can spread it.
 */
export type { CommentData } from '../fragments/commentData'

// ========================================
// Context Definition
// ========================================

/**
 * CommentContext is a thin Comments-specific wrapper over the shared
 * `EngagementContext`. Following the PostCardContext pattern, it provides only
 * the essential comment data plus reaction-error state; all other props
 * (channel, callbacks) are passed directly to components as needed.
 *
 * Comments carry no feature-specific `extra` payload (unlike Mentions, which
 * carry `replies`), so the shared context is instantiated with an empty extra.
 */
const { EngagementContext, EngagementContextProvider, useEngagementContext } =
  createEngagementContext<CommentData>({
    providerName: 'CommentContextProvider',
    hookName: 'useCommentContext',
  })

/** Stable empty `extra` payload — Comments have no feature-specific context. */
const NO_EXTRA: Record<string, never> = {}

export const CommentContext = EngagementContext

// ========================================
// Provider Component
// ========================================

export type CommentProviderProps = {
  comment: CommentData
  children: ReactNode
}

/**
 * CommentContextProvider - Provides comment context to child components
 *
 * This provider allows child components to access comment data without prop drilling.
 * Only core comment data is provided via context. All other props (channel, callbacks, state)
 * are passed directly to components.
 *
 * @example
 * ```tsx
 * <CommentContextProvider comment={comment}>
 *   <EngagementAvatar channel={channel} />
 *   <CommentHeader />
 *   <CommentContent />
 *   <CommentActions channel={channel} onDismiss={handleDismiss} />
 * </CommentContextProvider>
 * ```
 */
export const CommentContextProvider = ({
  comment,
  children,
}: CommentProviderProps): JSX.Element => (
  <EngagementContextProvider engagement={comment} extra={NO_EXTRA}>
    {children}
  </EngagementContextProvider>
)

CommentContextProvider.displayName = 'CommentContextProvider'

// ========================================
// Context Hook
// ========================================

/**
 * useCommentContext - Hook to access comment context
 *
 * This hook provides access to comment data within a CommentContextProvider tree.
 * It includes type safety and helpful error messaging for development.
 *
 * @throws {Error} When used outside of CommentContextProvider
 * @returns The comment context value
 *
 * @example
 * ```tsx
 * const CommentHeader = () => {
 *   const { comment } = useCommentContext()
 *   return <span>{comment.author.username}</span>
 * }
 * ```
 */
export const useCommentContext = (): {
  comment: CommentData
  reactionError: string | null
  setReactionError: (error: string | null) => void
} => {
  const { engagement, reactionError, setReactionError } = useEngagementContext()
  return { comment: engagement, reactionError, setReactionError }
}

// ========================================
// Utility Hook
// ========================================

/**
 * useCommentData - Convenience hook to get just the comment data
 *
 * This is a convenience hook that extracts just the comment data from the context.
 * Use this when you only need the comment data and not the full context object.
 *
 * @returns {CommentData} The comment data from context
 *
 * @example
 * ```tsx
 * const CommentContent = () => {
 *   const comment = useCommentData()
 *   return <p>{comment.content.text}</p>
 * }
 * ```
 */
export const useCommentData = (): CommentData => {
  const { engagement } = useEngagementContext()
  return engagement
}

/**
 * useCommentReactionError - Hook to access and set reaction error state
 *
 * This hook provides access to reaction error state for displaying
 * error messages when a reaction (like/unlike) fails. Paired with
 * `useMentionReactionError` on the Mentions side — keep the prefix
 * consistent so a future shared shell sees both names symmetrically.
 *
 * @returns Object with reactionError and setReactionError
 *
 * @example
 * ```tsx
 * const CommentReactions = () => {
 *   const { reactionError, setReactionError } = useCommentReactionError()
 *   // Set error on failure
 *   setReactionError('Failed to like comment')
 *   // Clear error on retry
 *   setReactionError(null)
 * }
 * ```
 */
export const useCommentReactionError = (): {
  reactionError: string | null
  setReactionError: (error: string | null) => void
} => {
  const { reactionError, setReactionError } = useEngagementContext()
  return { reactionError, setReactionError }
}
